import { Image } from '@/types/Image';
import { Room } from '@/types/Room';
import { Segment, SegmentFrom, SegmentTo } from '@/types/Segment';
import { Item, ItemCategory } from '@/types/Item';
import { SurveyServiceCompletionStatus } from '@/types/SurveyService';
import { ShipmentType } from '@/types/ShipmentType';

interface SegmentResponse extends Segment {
  survey_id: string;
}

interface ImageResponse {
  id: string;
  url: string;
}

interface RoomResponse {
  classification: string;
  created_at: string;
  id: string;
  room_name: string;
  images: Array<ImageResponse>;
}

interface ItemResponse {
  id: string;
  name: string;
  iso_code: string | null;
  weight: number | null;
  // TODO: `cube` should not be a string, remove once fixed on backend
  cube: number | string;
  length: number | null;
  width: number | null;
  height: number | null;
  room: RoomResponse;
  room_id: string;
  segment_id: string;
  comments: string;
  category: ItemCategory;
  going: number;
  not_going: number;
  packing: number;
  unpacking: number;
  images: Array<ImageResponse>;
  survey_id: string;
  definition_id: number;
  customer_reduced_quantity: boolean;
}

interface AddressResponse {
  street1: string;
  street2: string;
  region: string;
  locality: string;
  postal_code: string;
  country: string;
}

interface CustomerResponse {
  id: string;
  first_name: string;
  last_name: string;
}

interface OrderResponse {
  id: string;
  number: string;
  shipment_type: ShipmentType;
  origin: AddressResponse;
  destination: AddressResponse;
  customer: CustomerResponse;
}

interface MTOResponse {
  id: string;
  milmove_move_code: string;
  orders: Array<OrderResponse>;
}

interface ServiceResponse {
  id: string;
  status: string;
  completion_status: SurveyServiceCompletionStatus;
  subtype?: string;
}

interface SurveyResponse {
  id: string;
  is_complete: boolean;
  is_primary: boolean;
  items: Array<ItemResponse>;
  move_task_order: MTOResponse;
  name: string;
  rooms: Array<RoomResponse>;
  segments: Array<SegmentResponse>;
  service: ServiceResponse;
  service_id: string;
  survey_type: string;
}

interface SegmentResponse {
  from: SegmentFrom;
  id: string;
  order_id: string;
  survey_id: string;
  to: SegmentTo;
}

const mapSegment = ({ id, to, from, order_id }: SegmentResponse): Segment => ({
  id,
  to,
  from,
  orderId: order_id,
});

const mapImage = ({ url }: ImageResponse): Image => ({
  url,
});

const mapItem = (item: ItemResponse): Item => ({
  id: item.id,
  name: item.name,
  isoCode: item.iso_code,
  weight: item.weight,
  // TODO: Remove this parseFloat logic once API returns only decimals, not strings
  cube: typeof item.cube === 'string' ? parseFloat(item.cube) : item.cube,
  length: item.length,
  width: item.weight,
  height: item.height,
  roomId: item.room_id,
  segmentId: item.segment_id,
  comments: item.comments,
  category: item.category,
  going: item.going,
  notGoing: item.not_going,
  packing: item.packing,
  unpacking: item.unpacking,
  images: item.images.map(mapImage),
  definitionId: item.definition_id,
  customerReducedQuantity: item.customer_reduced_quantity,
});

const mapRoom = ({
  classification,
  id,
  room_name,
  images,
}: RoomResponse): Room => ({
  classification,
  id,
  name: room_name,
  images: images.map(mapImage),
});

const transformAddress = (address: AddressResponse) => ({
  address1: address.street1,
  address2: address.street2,
  city: address.locality,
  state: address.region,
  postalCode: address.postal_code,
  country: address.country,
});

const transformOrder = ({
  id,
  number,
  origin,
  destination,
  shipment_type,
}: OrderResponse) => ({
  id,
  number,
  shipmentType: shipment_type,
  origin: transformAddress(origin),
  destination: transformAddress(destination),
});

const transformMTO = ({ id, milmove_move_code, orders }: MTOResponse) => ({
  id,
  milmove_move_code: milmove_move_code,
  orders: orders.map(transformOrder),
});

const transformSurvey = (survey: SurveyResponse) => ({
  completed: survey.is_complete,
  id: survey.id,
  items: survey.items.map(mapItem),
  moveTaskOrderId: survey.move_task_order.id,
  name: survey.name,
  primary: survey.is_primary,
  rooms: survey.rooms.map(mapRoom),
  segments: survey.segments.map(mapSegment),
  serviceId: survey.service_id,
  surveyType: survey.survey_type,
});

const transformCustomer = ({
  id,
  first_name,
  last_name,
}: CustomerResponse) => ({
  id,
  firstName: first_name,
  lastName: last_name,
});

export const transformService = ({
  id,
  status,
  subtype,
  completion_status,
}: ServiceResponse) => ({
  id,
  status,
  subtype,
  completionStatus: completion_status,
});

export const transformResponse = (survey: SurveyResponse) => {
  return {
    move_task_order: transformMTO(survey.move_task_order),
    //TODO: This assumes that there will always be an order and all orders have the same customer
    customer: transformCustomer(survey.move_task_order.orders[0].customer),
    survey: transformSurvey(survey),
    service: transformService(survey.service),
  };
};
